import React, { useState } from 'react';
import {
  Lock, Infinity, Globe, Zap, BarChart3, ShieldCheck, Loader2,
  ArrowRight, Smartphone, Calendar, BookOpen, Hash, Sparkles, Repeat, Send
} from 'lucide-react';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import OnboardingModal from '../OnboardingModal';
import { useAuth } from '../../contexts/AuthContext';
import { pricingPlans } from '../../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STRIPE_API_URL = process.env.REACT_APP_BACKEND_URL;

// AutoPostr Pro DIY subscription features (centred / featured card)
const proFeatures = [
  { icon: Infinity, text: 'Unlimited posts' },
  { icon: Globe, text: 'All 7 social platforms' },
  { icon: Smartphone, text: 'Unlimited DIY posts on App and Web' },
  { icon: Zap, text: 'One-click posting' },
  { icon: BarChart3, text: 'Full content library' },
  { icon: ShieldCheck, text: 'Priority support' },
];

// Map a plain feature string to an icon
const iconForFeature = (text) => {
  const s = text.toLowerCase();
  if (s.includes('post') && s.includes('per day')) return Send;
  if (s.includes('platform')) return Globe;
  if (s.includes('automated') || s.includes('days of')) return Calendar;
  if (s.includes('library')) return BookOpen;
  if (s.includes('caption') || s.includes('hashtag')) return Hash;
  if (s.includes('scheduling') || s.includes('timing')) return Sparkles;
  if (s.includes('recycling')) return Repeat;
  if (s.includes('support')) return ShieldCheck;
  return Zap;
};

const PricingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { isAuthenticated, user, setShowAuthModal, getAuthHeaders } = useAuth();

  const handleGetAccess = async () => {
    // Token fallback - covers the race where AuthContext is still loading /auth/me
    // but a session token already exists in localStorage.
    const hasToken = (() => {
      try { return !!localStorage.getItem('autopostr_token'); } catch { return false; }
    })();
    const loggedIn = isAuthenticated || hasToken;

    if (!loggedIn) {
      setShowAuthModal('signin');
      return;
    }
    if (user?.is_pro) {
      window.location.href = '/dashboard';
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${STRIPE_API_URL}/api/stripe/create-pro-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ billing_period: billingPeriod, email: user?.email })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Error creating checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Error connecting to payment system. Please try again.');
    }
    setLoading(false);
  };

  const handleBulkGetStarted = (planName) => {
    setSelectedPlan(planName);
    setShowOnboarding(true);
  };

  const starter = pricingPlans.find(p => p.name === 'AutoPostr Starter');
  const premium = pricingPlans.find(p => p.name === 'AutoPostr Premium');

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header />

      <main className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-14">
            <span className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4 block">
              Pricing
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Choose the plan that fits you
            </h1>
            <p className="text-white/50 max-w-2xl mx-auto">
              Self-serve with AutoPostr Pro on App and Web, or let our team handle a full month of automated posting for you.
            </p>
          </div>

          {/* 3 Pricing Cards - all share AutoPostr Pro card design */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">

            {/* Card 1: AutoPostr Starter (Bulk Content) - matches Pro card design */}
            <BulkPlanCard
              testId="pricing-starter-card"
              title={starter?.name}
              accent="STARTER"
              accentColor="emerald"
              subtitle="Everything in Pro+ a starter dose of done-for-you posting."
              price={`£${starter?.price}`}
              priceUnit="/month"
              priceTag="Done-for-you · 30 days"
              features={starter?.features || []}
              ctaLabel="Get Started"
              onCta={() => handleBulkGetStarted(starter?.name)}
            />

            {/* Card 2: AutoPostr Pro (DIY Subscription) - Featured */}
            <FeaturedProCard
              billingPeriod={billingPeriod}
              setBillingPeriod={setBillingPeriod}
              loading={loading}
              isAuthenticated={isAuthenticated || (() => { try { return !!localStorage.getItem('autopostr_token'); } catch { return false; } })()}
              isPro={!!user?.is_pro}
              onCta={handleGetAccess}
              features={proFeatures}
            />

            {/* Card 3: AutoPostr Premium (Bulk Content - £50) - matches Pro card design */}
            <BulkPlanCard
              testId="pricing-premium-card"
              title={premium?.name}
              accent="PREMIUM"
              accentColor="purple"
              subtitle="Everything in Pro+ scaled for serious marketers and multi-page brands."
              price={`£${premium?.price}`}
              priceUnit="/month"
              priceTag="Done-for-you · 30 days"
              features={premium?.features || []}
              ctaLabel="Get Started"
              onCta={() => handleBulkGetStarted(premium?.name)}
            />
          </div>

          {/* Plan Comparison Note */}
          <div className="text-center mt-14 max-w-3xl mx-auto">
            <p className="text-[#737373] text-sm">
              <span className="text-white font-medium">AutoPostr Starter & Premium</span> are done-for-you bulk content services — our team posts for you for a full month.
              {' '}<span className="text-white font-medium">AutoPostr Pro</span> is a self-serve DIY subscription — post unlimited content yourself on App and Web.
            </p>
            <p className="text-[#737373] text-sm mt-3">
              Need a custom plan for your agency? <a href="mailto:hello@autopostr.co.uk" className="text-white font-medium underline underline-offset-2 hover:text-white/80 transition-colors">Contact us</a>.
            </p>
          </div>
        </div>
      </main>

      <Footer />

      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        selectedPlan={selectedPlan}
      />
    </div>
  );
};

// ===== Bulk plan card (Starter / Premium) - matches AutoPostr Pro card design =====
const BulkPlanCard = ({
  testId, title, accent, accentColor = 'emerald', subtitle, price, priceUnit, priceTag,
  features = [], ctaLabel, onCta
}) => {
  const accentClasses = {
    emerald: {
      badge: 'from-emerald-600/20 to-emerald-500/20 border-emerald-500/30',
      badgeText: 'text-emerald-400',
      iconBg: 'bg-emerald-500/10',
      iconText: 'text-emerald-400',
    },
    purple: {
      badge: 'from-purple-600/20 to-purple-500/20 border-purple-500/30',
      badgeText: 'text-purple-300',
      iconBg: 'bg-purple-500/10',
      iconText: 'text-purple-300',
    },
  }[accentColor] || {};

  return (
    <div
      data-testid={testId}
      className="relative rounded-3xl p-8 bg-gradient-to-br from-[#141414] to-[#0f0f0f] border border-white/10 hover:border-white/20 transition-all duration-300 flex flex-col"
    >
      {/* Top accent badge - same shape as PRO badge on Pro card */}
      <div className="flex justify-center mb-4">
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${accentClasses.badge} border`}>
          <Sparkles size={14} className={accentClasses.badgeText} />
          <span className={`${accentClasses.badgeText} font-semibold text-xs tracking-wider`}>{accent}</span>
        </div>
      </div>

      {/* Title + subtitle */}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
        <p className="text-sm text-[#737373] min-h-[40px]">{subtitle}</p>
      </div>

      {/* Big price block */}
      <div className="mb-6 p-4 rounded-xl border-2 border-white/10 bg-white/5">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-white">{price}</span>
          <span className="text-white/50 text-base">{priceUnit}</span>
        </div>
        {priceTag && <p className="text-white/50 text-xs mt-1">{priceTag}</p>}
      </div>

      {/* Features list - rounded square icons matching Pro card */}
      <ul className="space-y-3 mb-6 flex-1">
        {features.map((feature, idx) => {
          // First feature "Everything in Pro" gets gold/PRO emphasis
          const isProInclusion = feature.toLowerCase() === 'everything in pro';
          if (isProInclusion) {
            return (
              <li key={idx} className="flex items-center gap-3 p-2 -mx-1 rounded-lg bg-gradient-to-r from-yellow-500/10 to-yellow-500/5 border border-yellow-500/20">
                <div className="w-8 h-8 rounded-lg bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L4 9l8 13 8-13-8-7zm0 3.6l5.2 4.6-5.2 8.4L6.8 10.2 12 5.6z" fill="#EAB308"/>
                  </svg>
                </div>
                <span className="text-white text-sm font-semibold">
                  {feature}
                  <span className="ml-1.5 text-yellow-400/90 text-[10px] font-bold tracking-wider">+ MORE</span>
                </span>
              </li>
            );
          }
          const Icon = iconForFeature(feature);
          return (
            <li key={idx} className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg ${accentClasses.iconBg} flex items-center justify-center flex-shrink-0`}>
                <Icon size={15} className={accentClasses.iconText} />
              </div>
              <span className="text-white text-sm">{feature}</span>
            </li>
          );
        })}
      </ul>

      {/* Single bold CTA - matches Pro card */}
      <button
        onClick={onCta}
        data-testid={`${testId}-cta`}
        className="w-full py-4 rounded-full bg-emerald-400 hover:bg-emerald-500 text-black font-semibold text-base flex items-center justify-center gap-2 transition-all duration-200"
      >
        <Lock size={18} />
        {ctaLabel} — {price}{priceUnit}
      </button>

      <p className="text-center text-emerald-400/80 text-xs mt-3" data-testid={`${testId}-promo-hint`}>
        Have a promo or affiliate code? Apply it at checkout.
      </p>
    </div>
  );
};

// ===== Featured AutoPostr Pro DIY card (unchanged design from image) =====
const FeaturedProCard = ({ billingPeriod, setBillingPeriod, loading, isAuthenticated, isPro, onCta, features }) => (
  <div
    data-testid="pricing-autopostr-pro-card"
    className="relative rounded-3xl p-8 bg-gradient-to-br from-[#141414] to-[#0f0f0f] border-2 border-emerald-500/40 lg:scale-[1.03] lg:-my-2 shadow-2xl shadow-emerald-500/10 flex flex-col"
  >
    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
      <span className="px-4 py-1.5 rounded-full bg-emerald-500 text-black text-xs font-bold uppercase tracking-wider">
        Most Popular
      </span>
    </div>

    {/* PRO badge - matches design image */}
    <div className="flex justify-center mb-4 mt-2">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-600/20 to-yellow-500/20 border border-yellow-500/30">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L4 9l8 13 8-13-8-7zm0 3.6l5.2 4.6-5.2 8.4L6.8 10.2 12 5.6z" fill="#EAB308"/>
        </svg>
        <span className="text-yellow-500 font-semibold text-xs tracking-wider">PRO</span>
      </div>
    </div>

    <div className="text-center mb-6">
      <h3 className="text-2xl font-bold text-white mb-2">Unlock AutoPostr Pro</h3>
      <p className="text-sm text-[#737373] min-h-[40px]">
        Self-serve. DIY subscription both on App and Web. Post everywhere. Cancel anytime.
      </p>
    </div>

    {/* Billing Period Toggle */}
    <div className="space-y-3 mb-6">
      <button
        onClick={() => setBillingPeriod('monthly')}
        data-testid="pricing-monthly-option"
        className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${
          billingPeriod === 'monthly'
            ? 'border-emerald-500 bg-emerald-500/5'
            : 'border-white/10 bg-white/5 hover:border-white/20'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            billingPeriod === 'monthly' ? 'border-emerald-500' : 'border-white/30'
          }`}>
            {billingPeriod === 'monthly' && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-white text-base">Monthly</p>
            <p className="text-xl font-bold text-white mt-0.5">£9.99<span className="text-white/50 text-sm font-normal">/month</span></p>
            <p className="text-white/40 text-xs mt-0.5">Billed monthly. Cancel anytime.</p>
          </div>
        </div>
      </button>

      <button
        onClick={() => setBillingPeriod('yearly')}
        data-testid="pricing-yearly-option"
        className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left relative ${
          billingPeriod === 'yearly'
            ? 'border-emerald-500 bg-emerald-500/5'
            : 'border-white/10 bg-white/5 hover:border-white/20'
        }`}
      >
        <div className="absolute -top-2.5 right-3">
          <span className="px-2 py-0.5 rounded-full bg-yellow-500 text-black text-[10px] font-bold">
            Save 17%
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            billingPeriod === 'yearly' ? 'border-emerald-500' : 'border-white/30'
          }`}>
            {billingPeriod === 'yearly' && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-white text-base">Yearly</p>
            <p className="text-xl font-bold text-white mt-0.5">£99.99<span className="text-white/50 text-sm font-normal">/year</span></p>
            <p className="text-white/40 text-xs mt-0.5">2 months free. Billed annually.</p>
            <p className="text-yellow-400 text-xs font-semibold mt-1">Just £8.33/month — save £19.89/year</p>
          </div>
        </div>
      </button>
    </div>

    {/* Features - rounded square emerald icons */}
    <ul className="space-y-3 mb-6 flex-1">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
            <feature.icon size={15} className="text-emerald-400" />
          </div>
          <span className="text-white text-sm">{feature.text}</span>
        </li>
      ))}
    </ul>

    {/* CTA */}
    <button
      onClick={onCta}
      disabled={loading}
      data-testid="pricing-get-access-btn"
      className="w-full py-4 rounded-full bg-emerald-400 hover:bg-emerald-500 disabled:bg-emerald-400/50 text-black font-semibold text-base flex items-center justify-center gap-2 transition-all duration-200"
    >
      {loading ? (
        <>
          <Loader2 size={18} className="animate-spin" />
          Processing...
        </>
      ) : !isAuthenticated ? (
        <>
          <Lock size={18} />
          Sign in to Get Access
        </>
      ) : isPro ? (
        <>
          <ShieldCheck size={18} />
          You're Pro — Dashboard
        </>
      ) : (
        <>
          <Lock size={18} />
          Get Access — {billingPeriod === 'yearly' ? '£99.99/year' : '£9.99/month'}
        </>
      )}
    </button>

    <p className="text-center text-emerald-400/80 text-xs mt-3" data-testid="pricing-pro-promo-hint">
      Have a promo or affiliate code? Apply it at checkout.
    </p>
  </div>
);

export default PricingPage;
